/**
 * **一个普通话音节的校验与归一**（纯函数，前后端共用）。
 *
 * ## 为什么需要它（2026-10-20 上传文件那一轮）
 *
 * 家长现在把课本后面的三张表交给助手生成一个 **.txt**，文件里每个词元是
 * `字,拼音`（例如 `霜,shuāng`）。这份拼音是**照着课本誊录**的，它必须：
 *
 *   1. **真的写进了那个位置的读音**（`hanzi_listing.reading`），而不是靠同音字去猜；
 *   2. 写之前**校验它是一个合法的普通话音节** —— 手打的 `shuangx` / `shūang`
 *      （声调标错位置）如果不拦，听写时就会读错音，而界面上看不出来；
 *   3. **归一**成库里约定的那一套写法（小写、带声调符号、不含数字/空白/大写，
 *      见 `ck_hanzi_listing_reading_shape`）。
 *
 * ## 接受哪些写法（用户确认：至少 `shuāng` 与 `shuang`）
 *
 * | 写法 | 例子 | 归一结果 | 说明 |
 * | --- | --- | --- | --- |
 * | 带声调符号 | `shuāng` | `shuāng` | 课本上的标准写法 |
 * | 声调数字 | `shuang1` | `shuāng` | 1–4；`5` / `0` = 轻声，归一成不带声调 |
 * | 不带声调 | `shuang` | `shuang` | 允许（文件里像 `ma` / `ne` 这种轻声本来就是这么写的） |
 * | `ü` 的两种打写 | `lü` / `lv` / `lu:` | `lü` | `v` / `u:` 都是键盘输入 `ü` 的常用办法 |
 *
 * ⛔ **不猜声调**：只写 `shuang` 时**不**替你补成 `shuāng`。声调是"读哪个音"的
 * 一部分，猜错就是教错孩子；朗读那一层已经有既有的"按这个字自己的读音补声调"
 * 逻辑（见 `apps/web/src/components/dictation/importPreviewSpeech.ts`）。
 * ⚠️ 但 `shuang1` 这种**写明了声调**的数字写法会归一成 `shuāng`（它不是猜，是翻译）。
 *
 * ## 合法性的判据（"声母 + 韵母 + 声调"）
 *
 *   · **声母**：`b p m f d t n l g k h j q x zh ch sh r z c s y w`（`y/w` 是零声母的
 *     拼写，照普通话拼音方案当声母处理）；
 *   · **韵母**：`a o e ê ai ei ao ou an en ang eng er i ia ie iao iu iou ian in iang
 *     ing iong u ua uo uai ui uei uan un uen uang ueng ong ü üe üan ün ue`
 *     （`ue` 给 `jue/que/xue/yue`、`iu/iou`、`ui/uei` 这些缩写形式用）；
 *   · **零声母音节**：`a o e ê ai ei ao ou an en ang eng er`（外加 `m n ng hm hng`
 *     这几个叹词/鼻音音节）；
 *   · **声调符号的位置**（这条能抓住"看着像拼音、其实标错了"的手打错误）：
 *     有 `a` 标 `a`、没 `a` 有 `o` 标 `o`、没 `o` 有 `e` 标 `e`、
 *     `iu` 标 `u`、`ui` 标 `i`，其余标在唯一的元音上。
 *
 * ⚠️ 这是**结构校验**，不是"只认新华字典里的 400 个音节"：像 `shei` / `zhei` /
 *    `biang` 这类合法但不一定出现在内置字表里的拼写不会被误拒（**误拒会让家长
 *    录不进去**，比多收一个奇怪的音节更糟）。它挡的是明显不对的东西：
 *    数字没写好、字母写错、声母韵母拼不成、声调标错位置。
 */

import { splitSyllables } from './pinyin.js';

/** 声调：1–4 是四声，`0` = 没写 / 轻声。 */
export type PinyinTone = 0 | 1 | 2 | 3 | 4;

/** 一个音节的校验 + 归一结果。 */
export type PinyinSyllableResult =
  | {
      ok: true;
      /** 可以写库的读音：小写、带声调符号（或本来就没写声调）、不含数字/空白。 */
      reading: string;
      /** 去掉声调后的骨架（`ü` 写成 `ü`；朗读层找同音字时用得上）。 */
      base: string;
      tone: PinyinTone;
    }
  | { ok: false; reason: string };

/** 一个词（整词拼音）的校验 + 归一结果。 */
export type PinyinWordResult = { ok: true; reading: string } | { ok: false; reason: string };

/** 带声调符号的元音 → `{ base, tone }`。由下面的表生成，避免手抄错。 */
const MARKED_VOWELS = new Map<string, { base: string; tone: PinyinTone }>();
/** 元音 → 四声的符号（`applyToneMark()` 用）。 */
const TONE_MARKS: Record<string, readonly [string, string, string, string]> = {
  a: ['ā', 'á', 'ǎ', 'à'],
  o: ['ō', 'ó', 'ǒ', 'ò'],
  e: ['ē', 'é', 'ě', 'è'],
  i: ['ī', 'í', 'ǐ', 'ì'],
  u: ['ū', 'ú', 'ǔ', 'ù'],
  ü: ['ǖ', 'ǘ', 'ǚ', 'ǜ'],
};

for (const [base, marks] of Object.entries(TONE_MARKS)) {
  marks.forEach((marked, index) => {
    MARKED_VOWELS.set(marked, { base, tone: (index + 1) as PinyinTone });
  });
}

/**
 * 声母（**长的在前**：`zh/ch/sh` 必须先于 `z/c/s` 匹配，否则 `zhuang` 会被切成
 * `z` + `huang`）。
 */
const INITIALS: readonly string[] = [
  'zh',
  'ch',
  'sh',
  'b',
  'p',
  'm',
  'f',
  'd',
  't',
  'n',
  'l',
  'g',
  'k',
  'h',
  'j',
  'q',
  'x',
  'r',
  'z',
  'c',
  's',
  'y',
  'w',
];

/** 韵母（含 `iu/iou`、`ui/uei`、`ue` 这些缩写拼写）。 */
const FINALS: ReadonlySet<string> = new Set([
  'a',
  'o',
  'e',
  'ê',
  'ai',
  'ei',
  'ao',
  'ou',
  'an',
  'en',
  'ang',
  'eng',
  'er',
  'i',
  'ia',
  'ie',
  'iao',
  'iu',
  'iou',
  'ian',
  'in',
  'iang',
  'ing',
  'iong',
  'u',
  'ua',
  'uo',
  'uai',
  'ui',
  'uei',
  'uan',
  'un',
  'uen',
  'uang',
  'ueng',
  'ong',
  'ü',
  'üe',
  'üan',
  'ün',
  /** `jue/que/xue/yue` 里 `üe` 的写法。 */
  'ue',
]);

/** 零声母音节（`a` / `an` / `er`……以及几个鼻音叹词）。 */
const ZERO_INITIAL: ReadonlySet<string> = new Set([
  'a',
  'o',
  'e',
  'ê',
  'ai',
  'ei',
  'ao',
  'ou',
  'an',
  'en',
  'ang',
  'eng',
  'er',
]);

/** 没有"声母 + 韵母"结构的合法音节（鼻音 / 叹词）。 */
const SPECIAL_SYLLABLES: ReadonlySet<string> = new Set(['m', 'n', 'ng', 'hm', 'hng', 'ê']);

/** 一个音节里允许出现的"无声调字母"（`ü` 单独列出）。 */
function isBaseLetter(ch: string): boolean {
  return (ch >= 'a' && ch <= 'z') || ch === 'ü' || ch === 'ê';
}

/**
 * 这个音节该把声调标在哪个字母上（`-1` = 没有可标音的元音）。
 *
 * 规则就是小学教的那句：**有 a 不放过，没 a 找 o e，i u 并列标在后**。
 */
function toneMarkPosition(base: string): number {
  const a = base.indexOf('a');
  if (a >= 0) return a;
  const o = base.indexOf('o');
  if (o >= 0) return o;
  const e = base.indexOf('e');
  if (e >= 0) return e;
  const iu = base.indexOf('iu');
  if (iu >= 0) return iu + 1;
  const ui = base.indexOf('ui');
  if (ui >= 0) return ui + 1;
  for (let index = base.length - 1; index >= 0; index -= 1) {
    const ch = base[index];
    if (ch !== undefined && 'iuü'.includes(ch)) return index;
  }
  return -1;
}

/** 给无声调音节加上声调符号（`shuang` + 1 → `shuāng`）。 */
function applyToneMark(base: string, tone: PinyinTone): string | null {
  if (tone === 0) return base;
  const position = toneMarkPosition(base);
  if (position < 0) return null;
  const vowel = base[position];
  if (vowel === undefined) return null;
  const marks = TONE_MARKS[vowel];
  if (marks === undefined) return null;
  return `${base.slice(0, position)}${marks[tone - 1]}${base.slice(position + 1)}`;
}

/** 结构校验：`base`（已去声调）是不是"声母 + 韵母"能拼出来的普通话音节。 */
function structureProblem(base: string): string | null {
  if (SPECIAL_SYLLABLES.has(base)) return null;

  let initial = '';
  for (const candidate of INITIALS) {
    if (base.startsWith(candidate)) {
      initial = candidate;
      break;
    }
  }

  if (initial === '') {
    if (ZERO_INITIAL.has(base)) return null;
    return `「${base}」不是普通话的音节（没有声母时只能是 a/o/e/ai/ei/ao/ou/an/en/ang/eng/er 这些）`;
  }

  const final = base.slice(initial.length);
  if (final === '') return `「${base}」只有声母「${initial}」，没有韵母`;
  if (!FINALS.has(final)) {
    return `「${base}」的声母是「${initial}」、韵母是「${final}」，这个韵母不在普通话音节里（是不是打错了？）`;
  }
  return null;
}

/**
 * 校验并归一**一个**音节。
 *
 * @example
 *   normalizePinyinSyllable('shuāng') → { ok: true, reading: 'shuāng', base: 'shuang', tone: 1 }
 *   normalizePinyinSyllable('shuang1') → { ok: true, reading: 'shuāng', ... }
 *   normalizePinyinSyllable('shuang')  → { ok: true, reading: 'shuang', tone: 0 }
 *   normalizePinyinSyllable('shuangx') → { ok: false, reason: '……' }
 */
export function normalizePinyinSyllable(raw: string): PinyinSyllableResult {
  const trimmed = raw.trim().toLowerCase();
  if (trimmed === '') return { ok: false, reason: '拼音是空的' };

  /** `ü` 的两种键盘写法（`lv` / `lu:`）先归一成 `ü`。 */
  let text = trimmed.replace(/u:/g, 'ü').replace(/v/g, 'ü');

  /**
   * 声调数字在**末尾**（`shuang1`）。`58` 这种两位数字留给下面的"还有数字"判定。
   */
  let digitTone: PinyinTone | null = null;
  const digitMatch = /([0-5])$/.exec(text);
  if (digitMatch?.[1] !== undefined) {
    const value = Number.parseInt(digitMatch[1], 10);
    digitTone = value > 4 ? 0 : (value as PinyinTone);
    text = text.slice(0, -1);
  }

  let base = '';
  let markedTone: PinyinTone = 0;
  /** 声调符号落在 base 的第几个字母上（`-1` = 没有符号）。 */
  let markedPosition = -1;

  for (const ch of text) {
    if (ch === ' ') {
      return { ok: false, reason: '一个音节里不能有空格（拼音是连着写的）' };
    }
    if (ch >= '0' && ch <= '9') {
      return { ok: false, reason: `拼音里不能有数字「${ch}」（声调数字只能写在最后一位）` };
    }
    const marked = MARKED_VOWELS.get(ch);
    if (marked !== undefined) {
      if (markedTone !== 0) return { ok: false, reason: '一个音节只能标一个声调符号' };
      markedTone = marked.tone;
      markedPosition = base.length;
      base += marked.base;
      continue;
    }
    if (!isBaseLetter(ch)) {
      return { ok: false, reason: `「${ch}」不是拼音字母` };
    }
    base += ch;
  }

  if (base === '') return { ok: false, reason: '拼音里没有字母' };

  if (digitTone !== null && markedTone !== 0) {
    return {
      ok: false,
      reason: '同时写了声调符号和声调数字 —— 只写一种就行（shuāng 或 shuang1）',
    };
  }

  const problem = structureProblem(base);
  if (problem !== null) return { ok: false, reason: problem };

  if (markedTone !== 0) {
    const expected = toneMarkPosition(base);
    if (expected !== markedPosition) {
      const at = expected >= 0 ? base[expected] : undefined;
      return {
        ok: false,
        reason:
          at === undefined
            ? '这个音节没有可以标声调的元音'
            : `声调符号标错位置了 —— 应该标在「${at}」上（例如 ${applyToneMark(base, markedTone) ?? base}）`,
      };
    }
    return { ok: true, reading: text, base, tone: markedTone };
  }

  if (digitTone !== null && digitTone !== 0) {
    const marked = applyToneMark(base, digitTone);
    if (marked === null) return { ok: false, reason: '这个音节没有可以标声调的元音' };
    return { ok: true, reading: marked, base, tone: digitTone };
  }

  /**
   * 没有声调（包括 `shuang5` / `shuang0` 这种"明确写轻声"）：**原样保留无声调写法**。
   * ⛔ 不替你补声调（见文件头）。
   */
  return { ok: true, reading: base, base, tone: 0 };
}

/** 这个字符串是不是一个合法的普通话音节（写对了声调位置才算）。 */
export function isLegalPinyinSyllable(raw: string): boolean {
  return normalizePinyinSyllable(raw).ok;
}

/**
 * 校验并归一**一个词的整词拼音**：音节数必须与字数一致，逐音节校验。
 *
 * 归一后音节之间用**一个空格**分隔（与 `word.pinyin` / `ck_word_pinyin_shape` 一致）。
 */
export function normalizeWordPinyin(text: string, raw: string): PinyinWordResult {
  const chars = [...text.trim()];
  const syllables = splitSyllables(raw);
  if (syllables.length === 0) return { ok: false, reason: '拼音是空的' };
  if (syllables.length !== chars.length) {
    return {
      ok: false,
      reason: `音节数（${syllables.length}）与字数（${chars.length}）不一致`,
    };
  }
  const normalized: string[] = [];
  for (const [index, syllable] of syllables.entries()) {
    const result = normalizePinyinSyllable(syllable);
    if (!result.ok) {
      return { ok: false, reason: `第 ${index + 1} 个音「${syllable}」：${result.reason}` };
    }
    normalized.push(result.reading);
  }
  return { ok: true, reading: normalized.join(' ') };
}
